import { Component, Input } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';
import * as _ from 'lodash';
import { ProjectExpensesService } from '../project-expenses.service';
import {
  Project,
  CostItem,
  ItemGroup,
  MappedExpenseItem,
  CalculatedGroupingCost,
  CalculatedGrouping,
} from '@ops/shared-types';

@Component({
  selector: 'app-expense-overview',
  templateUrl: './expense-overview.component.html',
  styleUrls: ['./expense-overview.component.scss'],
})
export class ExpenseOverviewComponent {
  private _project;

  isLoading: boolean = true;

  @Input() set project(project: Project) {
    this._project = project;
    this.isLoading = true;
    this.loadProjectExpenses();
  }
  get project() {
    return this._project;
  }
  projectExpenses$: Observable<any>;

  constructor(private projectExpensesService: ProjectExpensesService) {}

  private mapCostItems(
    costItems: CostItem[],
    itemGroups: ItemGroup[]
  ): MappedExpenseItem[] {
    return costItems.map((costItem) => {
      const usedDate = new Date(
        (costItem.dateOfBill ??
          costItem.paymentDate ??
          costItem.createdAt) as Date
      );
      return {
        costItem: {
          ...costItem,
          costType: costItem.costType || '-',
          paymentMethod: costItem.paymentMethod || '-',
        },
        date: new Date(usedDate.getFullYear(), usedDate.getMonth()),
        itemGroup: itemGroups.find(
          (itemGroup) => itemGroup._id === costItem.itemGroup
        ),
      } as MappedExpenseItem;
    });
  }

  private groupMappedItems(mappedItems: MappedExpenseItem[]) {
    const groupedByDate = _.groupBy(mappedItems, 'date');
    return Object.keys(groupedByDate).map((date) => {
      const element = groupedByDate[date];
      return {
        expenseTypeCosts: _.groupBy(element, 'costItem.costType'),
        paymentMethodCosts: _.groupBy(element, 'costItem.paymentMethod'),
        departmentCosts: _.groupBy(element, 'itemGroup.department'),
        groupingDate: new Date(date),
      };
    });
  }

  private calculateCosts(costs: { [key: string]: MappedExpenseItem[] }) {
    return Object.keys(costs).map((costItem) => {
      const items = costs[costItem];
      const totals: CalculatedGroupingCost = {
        costName: costItem,
        paidAmount: 0,
        expectedAmount: 0,
      };
      items.forEach((item) => {
        if (item.costItem.costStatus === 'Paid') {
          totals.paidAmount += item.costItem.total ?? 0;
        } else {
          totals.expectedAmount += item.costItem.total ?? 0;
        }
      });
      return totals;
    });
  }

  private loadProjectExpenses(): void {
    this.projectExpenses$ = this.projectExpensesService
      .getOrCreateProjectCost(this.project._id, this.project.company)
      .pipe(
        switchMap((versionProjectId) =>
          combineLatest([
            this.projectExpensesService.getItemGroups(versionProjectId),
            this.projectExpensesService.getCostItems(versionProjectId),
          ])
        ),
        map(([itemGroups, costItems]) => {
          const mappedItems = this.mapCostItems(costItems, itemGroups);
          const allGroupings = this.groupMappedItems(mappedItems);
          return allGroupings.map((grouping) => {
            const calculatedGrouping: CalculatedGrouping = {
              expenseTypeCosts: this.calculateCosts(grouping.expenseTypeCosts),
              paymentMethodCosts: this.calculateCosts(
                grouping.paymentMethodCosts
              ),
              departmentCosts: this.calculateCosts(grouping.departmentCosts),
              recordedAmount: 0,
              budgetedAmount: 0,
              groupingDate: grouping.groupingDate,
            };
            calculatedGrouping.expenseTypeCosts.forEach((cost) => {
              calculatedGrouping.budgetedAmount += cost.expectedAmount;
              calculatedGrouping.recordedAmount += cost.paidAmount;
            });
            return calculatedGrouping;
          });
        }),
        tap(() => {
          this.isLoading = false;
        })
      );
  }
}
